import React, { forwardRef, useState, useImperativeHandle, useEffect } from "react";

export interface TokenisedCard {
    merchantCode: string;
    token: string;
    createdAt: Date;
    scheme: string;
    bin: string;
    last4: string;
    expiryMonth: string;
    expiryYear: string;
}

export interface PaymentError {
    id: string;
    code: string;
    detail: string;
    source?: {
        pointer: string;
    };
}

export interface SecurePayProps {
    /**
     * SecurePay container id
     */
    containerId?: string;

     /**
     * SecurePay container id
     */
     backGroundColor?: string;
    /**
     * SecurePay script id
     */
    scriptId?: string;
    /**
       * SecurePay component script URI
       */
    uri: string;
    /**
     * SecurePay API client id
     */
    clientId: string;

    /**
     * SecurePay API merchant code
     */
    merchantCode: string;

    /**
     * Callback method on load complete
     */
    onLoadComplete: () => void;

    /**
     * Callback method on tokenise success, used the tokenised card to make a request
     */
    onTokeniseSuccess: (tokenisedCard: TokenisedCard) => void;

    /**
     * Callback method on tokenise error, if you want to display these errors
     */
    onTokeniseError: (errors: PaymentError[]) => void;
}

export interface SecurePayMethods {
    tokenise: () => void,
    reset: () => void
}

export const SecurePay = forwardRef<SecurePayMethods, SecurePayProps>((args, ref) => {
    const {
        containerId = 'securepay-ui-container',
        scriptId = 'securepay-ui-js',
        ...props } = args;
    const [api, setApi] = useState<SecurePayMethods>();

    useImperativeHandle(ref, () => ({
        tokenise() {
            if (api)
                api.tokenise();
        },
        reset() {
            if (api) {
                api.reset();
            }
        },
    }));

    const init = ()=>{
        let temp = new (window as any).securePayUI.init({
            containerId: containerId,
            scriptId: scriptId,
            clientId: props.clientId,
            merchantCode: props.merchantCode,
            card: {
                allowedCardTypes: ["visa", "mastercard"],
                showCardIcons: true,
                onTokeniseSuccess: (tokenisedCard: TokenisedCard) => {
                    props.onTokeniseSuccess && props.onTokeniseSuccess(tokenisedCard);
                },
                onTokeniseError: (errorResponse: any) => {
                    let response = JSON.parse(errorResponse);
                    props.onTokeniseError && props.onTokeniseError(response.errors);
                },
            },
            style: {
                backgroundColor: props.backGroundColor? props.backGroundColor : '#f9f9f9',
                label: {
                    font: {
                        family:
                            '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                        size: "1rem",
                        color: "#313131",
                    },
                },
                input: {
                    font: {
                        family:
                            '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                        size: "1rem",
                        color: "#313131",
                    },
                },
            },
            onLoadComplete: () => {
                props.onLoadComplete && props.onLoadComplete();
            },
        });
        setApi(temp);
    }

    useEffect(() => {
        const existingScript = document.getElementById(scriptId);
        if (!existingScript) {
          const script = document.createElement("script");
          script.src = props.uri;
          script.id = scriptId;
          document.body.appendChild(script);
          script.onload = () => {
              init();
          };
        }
        else {
            init();
        }
    }, []);

    return <div id={containerId} />
});